import React, { useState, useEffect } from 'react';
import { X, Check, Coffee, CreditCard, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mockArtists, paymentMethods, mockTipHistory, getDebugUI } from '@/lib/mockData';
import { toast } from 'sonner';

// Payment method icons
const VenmoIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.5 2.25C20.4 3.9 20.8 5.6 20.8 7.65C20.8 12.85 16.35 19.5 12.8 23.25H4.2L1.2 3.5L8.85 2.8L10.45 16.05C11.95 13.5 13.8 9.5 13.8 6.5C13.8 4.55 13.45 3.2 12.85 2.1L19.5 2.25Z"/>
  </svg>
);

const CashAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h2.39c.14.89.77 1.5 1.77 1.5 1.11 0 1.67-.56 1.67-1.23 0-.79-.47-1.13-1.92-1.55-2.31-.66-3.54-1.63-3.54-3.48 0-1.63 1.25-2.86 3.02-3.19V5h2.67v1.75c1.58.36 2.72 1.39 2.85 3.1h-2.38c-.11-.74-.59-1.28-1.47-1.28-.82 0-1.42.45-1.42 1.14 0 .63.39 1.02 1.85 1.43 2.41.66 3.63 1.62 3.63 3.59 0 1.77-1.36 3.05-3.18 3.36z"/>
  </svg>
);

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.65a.766.766 0 0 1 .756-.65h6.09c3.018 0 5.236 1.65 4.824 4.848-.543 4.254-3.673 5.88-6.887 5.88H7.672l-.596 8.609zm11.578-17.34c.588 4.37-1.686 6.904-5.11 7.512.063-.62.112-1.25.138-1.89.35-2.877-.78-4.37-3.26-4.37H8.91l1.09-1.252h4.654c1.766 0 3.09.592 4 2z"/>
  </svg>
);

// Helper to get/set tips from localStorage in debug mode
const TIPS_STORAGE_KEY = 'exit88_tips';
const SELECTED_ARTIST_KEY = 'exit88_selected_artist';

const getTipsFromStorage = () => {
  try {
    const stored = localStorage.getItem(TIPS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

const saveTipToStorage = (tip) => {
  try {
    const existing = getTipsFromStorage();
    existing.push(tip);
    localStorage.setItem(TIPS_STORAGE_KEY, JSON.stringify(existing));
  } catch (e) {
    console.error('Failed to save tip to storage');
  }
};

// Get/set selected artist from localStorage
const getSelectedArtistFromStorage = (eventId) => {
  try {
    const key = `${SELECTED_ARTIST_KEY}_${eventId}`;
    return localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};

const saveSelectedArtistToStorage = (eventId, artistId) => {
  try {
    const key = `${SELECTED_ARTIST_KEY}_${eventId}`;
    localStorage.setItem(key, artistId);
  } catch (e) {
    console.error('Failed to save selected artist');
  }
};

export function TipArtistSheet({ isOpen, onClose, eventId, eventArtists = [], onTipSent }) {
  const isDebugMode = getDebugUI();
  
  // Load selected artist from localStorage or use first artist
  const getInitialArtist = () => {
    const stored = getSelectedArtistFromStorage(eventId);
    if (stored && eventArtists.some(a => a.id === stored)) {
      return stored;
    }
    return eventArtists[0]?.id || '';
  };
  
  const [selectedArtist, setSelectedArtist] = useState(getInitialArtist);
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update selected artist in localStorage when it changes
  useEffect(() => {
    if (selectedArtist && eventId) {
      saveSelectedArtistToStorage(eventId, selectedArtist);
    }
  }, [selectedArtist, eventId]);

  // Reset selected artist when eventArtists changes (on open)
  useEffect(() => {
    if (isOpen && eventArtists.length > 0) {
      const stored = getSelectedArtistFromStorage(eventId);
      if (stored && eventArtists.some(a => a.id === stored)) {
        setSelectedArtist(stored);
      } else if (!selectedArtist) {
        setSelectedArtist(eventArtists[0].id);
      }
    }
  }, [isOpen, eventArtists, eventId, selectedArtist]);

  if (!isOpen) return null;

  // Get full artist details from mockArtists, or use eventArtists directly
  const artists = eventArtists.length > 0 
    ? eventArtists.map(ea => {
        const fullArtist = mockArtists.find(a => a.id === ea.id);
        return fullArtist || ea;
      })
    : mockArtists.slice(0, 3); // Fallback: show first 3 artists if none specified

  // Hide tipping UI gracefully if no artists exist
  if (artists.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center" data-testid="tip-artist-sheet">
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <div className="relative w-full lg:max-w-md bg-card rounded-t-3xl lg:rounded-2xl overflow-hidden animate-in slide-in-from-bottom p-6 text-center">
          <p className="text-muted-foreground">No artists available for tipping at this event.</p>
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }

  const selectedArtistData = artists.find(a => a.id === selectedArtist) || artists[0];

  // Get tip links from artist (admin-controlled)
  const tipLinks = selectedArtistData?.tipLinks || {};

  const handleTip = (method) => {
    if (!selectedArtist) {
      toast.error('Please select an artist to tip');
      return;
    }

    // Get the URL for this method from admin-configured tipLinks
    const url = tipLinks[method];
    
    if (!url) {
      toast.error(`${selectedArtistData?.name} hasn't set up ${method} tipping yet`);
      return;
    }

    // Open the tip URL in a new tab
    window.open(url, '_blank');

    // Create tip record for simulation
    const tipAmount = amount ? parseFloat(amount) : 0;
    const tipId = Math.random().toString(36).substring(2, 15);
    const newTip = {
      id: `tip-${tipId}`,
      fromUserId: 'user-001',
      senderName: 'Daniel U.',
      fromUserName: 'Daniel U.',
      toArtistId: selectedArtist,
      toArtistName: selectedArtistData?.name,
      artistName: selectedArtistData?.name,
      amount: tipAmount,
      method: method,
      timestamp: new Date().toISOString(),
      message: '',
      eventId: eventId
    };

    // Add to mock tip history
    if (mockTipHistory[eventId]) {
      mockTipHistory[eventId].push(newTip);
    }

    // Save to localStorage in debug mode
    if (isDebugMode) {
      saveTipToStorage(newTip);
    }

    // Show confirmation toast
    toast.success(`Thanks for supporting ${selectedArtistData?.name}!`, {
      description: tipAmount > 0 ? `Tip amount: $${tipAmount}` : 'Opening payment link...'
    });
    
    // Callback to parent with tip info (for chat integration)
    if (onTipSent && tipAmount > 0) {
      onTipSent(newTip);
    }
    
    setAmount('');
    onClose();
  };

  // Check if method is available (admin has set up the URL)
  const isMethodAvailable = (methodId) => {
    return !!tipLinks[methodId];
  };

  // Count available methods
  const availableMethods = ['paypal', 'venmo', 'cashApp', 'customTip'].filter(m => tipLinks[m]);

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center" data-testid="tip-artist-sheet">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      
      {/* Sheet - Full width on mobile, centered modal on desktop */}
      <div className="relative w-full lg:max-w-md bg-card rounded-t-3xl lg:rounded-2xl overflow-hidden animate-in slide-in-from-bottom max-h-[90vh] overflow-y-auto">
        {/* Handle bar for mobile */}
        <div className="lg:hidden w-12 h-1.5 bg-gray-600 rounded-full mx-auto mt-3" />
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className="text-xl font-bold text-white">Tip Artist</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Artist Selector */}
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground mb-2">Select artist to tip</p>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {artists.map(artist => (
              <button
                key={artist.id}
                onClick={() => setSelectedArtist(artist.id)}
                className="flex flex-col items-center flex-shrink-0"
                data-testid={`select-artist-${artist.id}`}
              >
                <div className="relative">
                  <img 
                    src={artist.image} 
                    alt={artist.name}
                    className={`w-16 h-16 rounded-full object-cover border-2 transition-colors ${
                      selectedArtist === artist.id ? 'border-primary' : 'border-transparent'
                    }`}
                  />
                  {selectedArtist === artist.id && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
                <span className="text-xs text-muted-foreground mt-2 max-w-[70px] truncate">
                  {artist.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Amount Input (optional) */}
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground mb-2">Tip amount (optional)</p>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">$</span>
            <Input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-secondary border-border h-14 pl-8 text-lg text-white placeholder:text-muted-foreground"
              data-testid="tip-amount-input"
            />
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground mb-3">Choose payment method</p>
          
          {availableMethods.length === 0 ? (
            <div className="text-center py-6 bg-secondary/50 rounded-lg">
              <p className="text-muted-foreground">No payment methods available for this artist</p>
              <p className="text-xs text-muted-foreground mt-1">The artist has not configured their tipping links yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {/* PayPal */}
              {tipLinks.paypal && (
                <Button
                  onClick={() => handleTip('paypal')}
                  disabled={isSubmitting}
                  className="h-12 flex items-center justify-center gap-2 font-medium bg-white hover:bg-gray-100 text-[#003087]"
                  data-testid="tip-paypal-btn"
                >
                  <PayPalIcon />
                  PayPal
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </Button>
              )}

              {/* Venmo */}
              {tipLinks.venmo && (
                <Button
                  onClick={() => handleTip('venmo')}
                  disabled={isSubmitting}
                  className="h-12 flex items-center justify-center gap-2 font-medium bg-[#3D95CE] hover:bg-[#3D95CE]/90 text-white"
                  data-testid="tip-venmo-btn"
                >
                  <VenmoIcon />
                  Venmo
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </Button>
              )}

              {/* Cash App */}
              {tipLinks.cashApp && (
                <Button
                  onClick={() => handleTip('cashApp')}
                  disabled={isSubmitting}
                  className="h-12 flex items-center justify-center gap-2 font-medium bg-[#00D632] hover:bg-[#00D632]/90 text-white"
                  data-testid="tip-cashapp-btn"
                >
                  <CashAppIcon />
                  Cash App
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </Button>
              )}

              {/* Custom Tip (admin-configured) */}
              {tipLinks.customTip && (
                <Button
                  onClick={() => handleTip('customTip')}
                  disabled={isSubmitting}
                  className="h-12 flex items-center justify-center gap-2 font-medium bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  data-testid="tip-custom-btn"
                >
                  <LinkIcon className="w-5 h-5" />
                  Other
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Safe space for mobile */}
        <div className="h-6 lg:h-4" />
      </div>
    </div>
  );
}

export default TipArtistSheet;
